import React, {useRef} from 'react';

import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import Tooltip from '@mui/material/Tooltip';

import EmailChip from './EmailChip';

const SeatsBar = ({available, total}) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-menu bg-panels-normal text-sm text-main-normal">
        <PeopleOutlineIcon style={{fontSize: 18}} />
        <span>{available} of {total} seats available</span>
    </div>
);

const ValidationMessage = ({count, text, type, testId}) => {
    if (!count) return null;
    const isError = type === 'error';
    const bgClass = isError
        ? 'bg-[color-mix(in_srgb,var(--leavedeletenew-normal-color)_8%,transparent)]'
        : 'bg-[color-mix(in_srgb,var(--caution-warning-color)_8%,transparent)]';
    const textClass = isError ? 'text-leavedeletenew-normal' : 'text-[var(--caution-warning-color)]';
    const Icon = isError ? ErrorOutlineIcon : InfoOutlinedIcon;

    return (
        <div data-testid={testId} className={`flex items-center gap-2 px-3 py-2 rounded-menu text-sm ${bgClass} ${textClass}`}>
            <Icon style={{fontSize: 16}} />
            <span>{count} {text}</span>
        </div>
    );
};

const EmailInputStep = ({emailHook, available, total, onUploadCsv, isUploading, exceedsSeats}) => {
    const inputRef = useRef(null);
    const {emails, inputValue, setInputValue, removeEmail, handleKeyDown, handlePaste, handleBlur, invalidCount, duplicateCount} = emailHook;

    return (
        <div className="flex flex-col gap-4">
            <SeatsBar available={available} total={total} />

            <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-main-normal">Email addresses</label>
                <div
                    className="flex flex-wrap gap-1.5 p-2 min-h-[44px] max-h-[180px] overflow-y-auto rounded-menu border border-panels-normal bg-windowsandmenus-normal cursor-text special-scroll"
                    onClick={() => inputRef.current?.focus()}
                >
                    {emails.map((email, i) => (
                        <EmailChip
                            key={email.address}
                            email={email.address}
                            isInvalid={email.isInvalid}
                            isDuplicate={email.isDuplicate}
                            onRemove={() => removeEmail(i)}
                        />
                    ))}
                    <input
                        ref={inputRef}
                        data-testid="invite-email-input"
                        className="flex-1 min-w-[150px] outline-none bg-transparent text-sm text-main-normal placeholder:text-main-disabled"
                        placeholder={emails.length ? 'Add more...' : 'Type email and press Enter, comma, or space'}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onBlur={handleBlur}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <ValidationMessage count={invalidCount} text="invalid emails - check format" type="error" testId="invite-invalid-email-warning" />
                <ValidationMessage count={duplicateCount} text="already in workspace" type="warning" testId="invite-duplicate-warning" />
                {exceedsSeats && (
                    <ValidationMessage count={1} text={`Not enough seats - only ${available} available`} type="error" testId="invite-exceeds-seats-warning" />
                )}
            </div>

            {onUploadCsv && (
                <Tooltip
                    title="Quick bulk import: Upload a file and we'll extract all email addresses"
                    placement="top"
                    componentsProps={{tooltip: {
                        className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input',
                    }}}
                >
                    <div
                        className={`flex items-center gap-1.5 self-end text-sm ${isUploading ? 'text-main-disabled cursor-default' : 'text-main-normal cursor-pointer hover:underline'}`}
                        onClick={isUploading ? undefined : onUploadCsv}
                    >
                        <FileUploadOutlinedIcon style={{fontSize: 16}} />
                        <span>{isUploading ? 'Extracting emails...' : 'Upload CSV/Excel'}</span>
                    </div>
                </Tooltip>
            )}
        </div>
    );
};

export default EmailInputStep;
